import {flowRight as compose} from 'lodash';
import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {Query} from 'react-apollo';
import {Col, Container, Row} from 'react-bootstrap';
import {withTranslation} from 'react-i18next';
import PopIn from '../../../common/components/PopIn';
import DateRangePicker from '../../../common/components/Dates/DateRangePicker';
import SelectCivility from '../../../common/components/User/SelectCivility';
import gql from 'graphql-tag';
import {CancelButton, Div, FooterRow, GradientCol, TitleButton} from '../../../common/components/StyledComp';
import moment from 'moment';
import {withRouter} from 'react-router-dom';
import Error from '../../../common/components/Error/Error';
import styled from 'styled-components';
import {PulseLoader} from 'react-spinners';
import isAfterDay from 'react-dates/lib/utils/isAfterDay';
import Badge from '../../../common/components/Badge';
import {BADGE_COLORS} from '../../../common/constants';
import {Alert} from 'reactstrap';
import {numberFormatThousands} from '../../../utils';

const BuyNotificationsBtn = styled(TitleButton)`
  margin-bottom: 15px;
  height: auto;
  width: auto;
`;

const MargeDiv = styled.div`
   margin: 5px;
`;

const SubTitle = styled.h4`
  margin-top: 20px;
`;

const CenteredCol = styled(Col)`
  text-align: center;
`;

const SeparatedCol = styled(Col)`
  border-right: 1px solid #ccc;
`;

const MargedAlert = styled(Alert)`
  margin-top: 10px;
`;

const GRAPHQL_QUERY_CURRENT_MERCHANT_CAMPAIGNS_SUMMARY = gql`
  query currentMerchantCampaignSummary($filtersInput: CampaignFiltersInput!) {
    campaignSummary(filtersInput: $filtersInput){
      id
      totalUsers
      totalUsersWithLoyaltyCard
      totalUsersWithoutLoyaltyCard
      canCreate
    }
  }
`;

const AddCampaignModal = ({t, merchant, history, ButtonComponent}) => {

  const [modalVisible, setModalVisible] = useState(false);
  const [civilityFilter, setCivilityFilter] = useState('all');
  const [{startDate, endDate}, setDateFilter] = useState({});

  useEffect(() => {
    if (!modalVisible) {
      setCivilityFilter('all');
      setDateFilter({});
    }
  }, [merchant, modalVisible]);

  const filtersInput = {
    filterUserStartDate: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
    filterUserEndDate: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
    filterUserCivility: civilityFilter !== 'all' && civilityFilter !== 'default' ? civilityFilter : null,
  };

  const onCreateCampaign = (event, summary) => {
    event.preventDefault();
    history.push('/admin/campaign/add', {summary, filtersInput, merchant});
  };

  const isOutsideDateRange = day => isAfterDay(day, moment());

  return (
    <>
      <ButtonComponent onClick={() => {setModalVisible(true);}}>
        {t('addNewCampaignBtn')}
      </ButtonComponent>

      <PopIn visible={modalVisible} setModalVisible={setModalVisible} title={t('addCampaign.title')}>
        <Container>
          <SubTitle>{`${t('selectUsers')} : `}</SubTitle>
          <Row>
            <Col xs={8}>
              <Div className="attribute-name">{`${t('period')} : `}</Div>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onDatesChange={setDateFilter}
                isOutsideRange={isOutsideDateRange}
              />
            </Col>
            <Col xs={4}>
              <Div className="attribute-name">{`${t('civility')} : `}</Div>
              <MargeDiv>
                <SelectCivility
                  value={civilityFilter}
                  allowNull
                  onChange={e => {setCivilityFilter(e.target.value);}}
                />
              </MargeDiv>
            </Col>
          </Row>
          {(filtersInput.filterUserStartDate === null && filtersInput.filterUserEndDate === null && civilityFilter === 'all') && (
            <MargedAlert color="info">{t('addCampaign.noSelectedInputs')}</MargedAlert>
          )}

          <SubTitle>{`${t('reachableClients')} : `}</SubTitle>
          <Query query={GRAPHQL_QUERY_CURRENT_MERCHANT_CAMPAIGNS_SUMMARY} variables={{filtersInput}}>
            {({error, loading, data}) => {

              if (error) {
                return <Error error={t('unknownError')} />;
              }

              const summary = data && data.campaignSummary ? data.campaignSummary : null;
              return (
                <>
                  {loading ? (
                    <PulseLoader/>
                  ) : (
                    <>
                      <Row>
                        <SeparatedCol>
                          <Row>
                            <Col xs={8}>{`${t('usersWithLoyaltyCard')} : `}</Col>
                            <CenteredCol xs={4}>{numberFormatThousands(summary.totalUsersWithLoyaltyCard)}
                            </CenteredCol>
                          </Row>
                          <Row>
                            <Col xs={8}>{`${t('usersWithoutLoyaltyCard')} : `}</Col>
                            <CenteredCol xs={4}>{numberFormatThousands(summary.totalUsersWithoutLoyaltyCard)}</CenteredCol>
                          </Row>
                        </SeparatedCol>
                        <CenteredCol>
                          <BuyNotificationsBtn onClick={() => {history.push('/admin/products/buy/notification');}}>
                            {t('buyNotifications')}
                          </BuyNotificationsBtn>
                        </CenteredCol>
                      </Row>
                      <Row>
                        <SeparatedCol>
                          <Row>
                            <Col xs={8}><strong>{`${t('total')} : `}</strong></Col>
                            <CenteredCol xs={4}>
                              <Badge color={summary.totalUsers > 0 ? BADGE_COLORS.GREEN : BADGE_COLORS.RED}>
                                {numberFormatThousands(summary.totalUsers)}
                              </Badge>
                            </CenteredCol>
                          </Row>
                        </SeparatedCol>
                        <Col>
                          <Row>
                            <Col xs={8}>{`${t('availableNotifications')} : `}</Col>
                            <CenteredCol xs={4}>
                              <Badge color={summary.totalUsers < merchant.totalAvailableNotification ? BADGE_COLORS.GREEN : BADGE_COLORS.RED}>
                                {numberFormatThousands(merchant.totalAvailableNotification)}
                              </Badge>
                            </CenteredCol>
                          </Row>
                        </Col>
                      </Row>
                    </>
                  )}
                  <FooterRow>
                    <GradientCol lg={{span: 6, offset: 3}}>
                      <div className="popin-buttons mt-3">
                        <TitleButton disabled={!summary || !summary.canCreate} onClick={e => onCreateCampaign(e, summary)}>
                          {t('addCampaign.createCampaign')}
                        </TitleButton>
                        <CancelButton onClick={() => {setModalVisible(false);}} style={{marginTop: 0, width: 'auto'}}>
                          {t('cancel')}
                        </CancelButton>
                      </div>
                    </GradientCol>
                  </FooterRow>
                </>
              );
            }}
          </Query>
        </Container>
      </PopIn>
    </>
  );
};

AddCampaignModal.defaultProps = {
  ButtonComponent: TitleButton,
};

AddCampaignModal.propTypes = {
  t: PropTypes.func.isRequired,
  merchant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    totalAvailableNotification: PropTypes.number.isRequired,
  }).isRequired,
  history: PropTypes.object.isRequired,
  ButtonComponent: PropTypes.any,
};

export default compose(
  withTranslation('campaign'),
  withRouter,
)(AddCampaignModal);
